import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import LandingHeader from '../../components/LandingHeader/LandingHeader'
import LandingFooter from '../../components/LandingFooter/LandingFooter'
import Turnstile from '../../components/Turnstile'
import Seo from '../../components/Seo/Seo'
import { MAROON, INK, DARK, CREAM, EXP } from './FormLayout'
import {
  STAGE_COLORS,
  PIPELINE_LEGEND,
  PIPELINE_GROUPS,
  PIPELINE_DISCLAIMER_LEAD,
  PIPELINE_DISCLAIMER_LINK,
  PIPELINE_DISCLAIMER_REST,
  FDA_PATHWAY_FOOTNOTE_LEAD,
  FDA_PATHWAY_FOOTNOTE_REST,
  stageStatusLabel,
} from './pipelineData'
import { captureLead, LEAD_SEGMENTS } from '../../utils/leadCapture'
import { notifySubscribe } from '../../utils/subscribeNotify'
import { heroSrcSet, HERO_SIZES } from '../../utils/responsiveImage.mjs'
import './FormPage.css'
import './Products.css'

const publicUrl = (import.meta.env.BASE_URL || '').replace(/\/$/, '')
const heroImageUrl = `${publicUrl}/images/products-hero.jpg`
const bioplyImageUrl = `${publicUrl}/images/peptide-vials.jpg`

const eyebrowStyle = (color = MAROON) => ({
  fontSize: '12.5px',
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  color,
  fontWeight: 700,
  margin: 0,
})

// The three BIO-PLY™ facts, shown one at a time. Three columns inside the
// available-now card left each fact ~150px, which cramped the copy; a snap
// carousel gives each one the full measure.
const BIO_PLY_FACTS = [
  {
    title: 'Triple action',
    body: 'Antimicrobial, anti-inflammatory, and pro-healing in one treatment.',
  },
  {
    title: 'Off the shelf',
    body: 'One vial, no prep — ready to use in the clinic.',
  },
  {
    title: 'FDA pathway',
    body: 'Advancing through FDA-CVM regulatory review.',
  },
]

const FACT_INTERVAL_MS = 5000

function BioPlyFacts() {
  const trackRef = useRef(null)
  const [active, setActive] = useState(0)
  // Auto-advance pauses while the visitor is reading (hover or keyboard focus),
  // so it never yanks a card away mid-sentence.
  const [paused, setPaused] = useState(false)

  // Derive the active dot from scroll position rather than tracking it on click,
  // so a swipe or a trackpad scroll keeps the dots honest too. Measured off
  // getBoundingClientRect, not offsetLeft — the cards' offsetParent is an
  // ancestor of the track, so offsetLeft is not track-relative.
  const handleScroll = () => {
    const track = trackRef.current
    if (!track) return
    const mid = track.getBoundingClientRect().left + track.clientWidth / 2
    const cards = Array.from(track.children)
    const i = cards.findIndex((c) => c.getBoundingClientRect().right > mid)
    setActive(i === -1 ? cards.length - 1 : i)
  }

  const goTo = (i) => {
    const track = trackRef.current
    const card = track?.children[i]
    if (!card) return
    const delta = card.getBoundingClientRect().left - track.getBoundingClientRect().left
    track.scrollBy({ left: delta, behavior: 'smooth' })
  }

  // Cycle through the facts on a timer. Keyed off `active`, so the countdown
  // restarts whenever the visitor scrolls or taps a dot rather than firing part
  // way through the card they just chose. Skipped entirely under reduced motion.
  useEffect(() => {
    if (paused) return undefined
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined
    const id = setTimeout(
      () => goTo((active + 1) % BIO_PLY_FACTS.length),
      FACT_INTERVAL_MS,
    )
    return () => clearTimeout(id)
  }, [active, paused])

  // Pause on keyboard focus only. A mouse click also focuses the dot, and
  // pausing on that left the carousel stalled for good after a single tap.
  const handleFocusCapture = (e) => {
    if (e.target.matches?.(':focus-visible')) setPaused(true)
  }

  return (
    <div
      className="prod-facts"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={() => setPaused(false)}
    >
      <div
        className="prod-facts-track"
        ref={trackRef}
        onScroll={handleScroll}
        tabIndex={0}
        role="group"
        aria-label="BIO-PLY key facts"
      >
        {BIO_PLY_FACTS.map((fact) => (
          <div className="prod-fact" key={fact.title}>
            <div className="prod-fact-title" style={{ fontFamily: EXP }}>{fact.title}</div>
            <p className="prod-fact-body">{fact.body}</p>
          </div>
        ))}
      </div>
      <div className="prod-facts-dots">
        {BIO_PLY_FACTS.map((fact, i) => (
          <button
            type="button"
            key={fact.title}
            className={`prod-facts-dot${i === active ? ' is-active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Show ${fact.title}`}
            aria-current={i === active}
          />
        ))}
      </div>
    </div>
  )
}

export default function Products() {
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const turnstileRef = useRef(null)
  const fallbackRef = useRef(null)

  useEffect(() => {
    const els = document.querySelectorAll('.q-reveal')
    if (!('IntersectionObserver' in window)) {
      els.forEach((e) => e.classList.add('q-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('q-in')
            io.unobserve(en.target)
          }
        }),
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' },
    )
    els.forEach((e) => io.observe(e))
    fallbackRef.current = setTimeout(() => els.forEach((e) => e.classList.add('q-in')), 1600)
    return () => {
      io.disconnect()
      clearTimeout(fallbackRef.current)
    }
  }, [])

  const handleSubscribe = async (e) => {
    e.preventDefault()
    if (!captchaToken || submitting) return
    setSubmitting(true)
    setError('')

    const { error: captureError } = await captureLead({
      email,
      segment: LEAD_SEGMENTS.PRODUCT_NOTIFICATION,
      source: 'products',
      payload: { list: 'product_pipeline' },
      token: captchaToken,
    })

    // The Turnstile token is single-use; clear it whatever the outcome so a
    // retry re-challenges.
    turnstileRef.current?.reset()
    setCaptchaToken('')
    setSubmitting(false)

    if (captureError) {
      setError("Something went wrong adding you to the list. Please try again.")
      return
    }

    // Email the subscriber their "you're on the list" confirmation. Fire-and-
    // forget: the signup is already saved, so a mail hiccup must not block the
    // success state.
    notifySubscribe({ email })

    setSubscribed(true)
  }

  return (
    <div style={{ background: CREAM, color: INK, overflowX: 'clip', fontFamily: "'Archivo',sans-serif", paddingTop: '64px', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Seo
        title="Products & Pipeline | Qentoros"
        description="From our flagship product BIO-PLY™ to a growing pipeline of innovative biologics, every Qentoros therapy is guided by rigorous science, manufactured to uncompromising quality standards, and developed through meaningful clinical results."
        path="/products"
      />
      <LandingHeader />

      {/* Grows to fill any viewport taller than the content so the footer
          stays pinned to the bottom. */}
      <main id="main" tabIndex={-1} style={{ flex: '1 0 auto' }}>
      {/* HERO */}
      <section
        style={{
          position: 'relative',
          minHeight: 'clamp(460px, 52vh, 580px)',
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          padding: 'clamp(56px,7vw,96px) 0 clamp(48px,6vw,80px)',
        }}
      >
        <picture>
          <source type="image/avif" srcSet={heroSrcSet(heroImageUrl, 'avif')} sizes={HERO_SIZES} />
          <source type="image/webp" srcSet={heroSrcSet(heroImageUrl, 'webp')} sizes={HERO_SIZES} />
          <img
            src={heroImageUrl}
            alt=""
            aria-hidden="true"
            width="1600"
            height="901"
            fetchPriority="high"
            decoding="async"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 34%' }}
          />
        </picture>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'linear-gradient(105deg, rgba(122,18,48,0.44) 0%, rgba(122,18,48,0.20) 48%, rgba(122,18,48,0.05) 80%), linear-gradient(to top, rgba(22,8,12,0.88) 0%, rgba(22,8,12,0.50) 34%, rgba(22,8,12,0.14) 60%, rgba(22,8,12,0) 100%), linear-gradient(100deg, rgba(22,8,12,0.52) 0%, rgba(22,8,12,0.16) 42%, rgba(22,8,12,0) 72%)',
          }}
        />
        <div style={{ position: 'relative', zIndex: 2, width: '100%', padding: '0 7vw' }}>
          <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              padding: '7px 14px',
              border: '1px solid rgba(255,255,255,.34)',
              borderRadius: '100px',
              marginBottom: '22px',
              background: 'rgba(255,255,255,.10)',
              backdropFilter: 'blur(6px)',
            }}
          >
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E79A8C' }} />
            <span style={{ fontSize: '12px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,.88)', fontWeight: 600 }}>
              Products &amp; Pipeline
            </span>
          </div>
          <h1
            style={{
              fontFamily: EXP,
              fontWeight: 700,
              fontSize: 'clamp(40px,5.6vw,74px)',
              lineHeight: 1,
              letterSpacing: '-.025em',
              color: '#fff',
              marginBottom: '20px',
              textWrap: 'balance',
              maxWidth: '760px',
            }}
          >
            One platform. A growing pipeline of{' '}
            <span
              style={{
                background: 'linear-gradient(100deg, #ff8a4c 0%, var(--color-primary-orange) 50%, #e0533f 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.4))',
              }}
            >
              therapeutics.
            </span>
          </h1>
          <p style={{ fontSize: 'clamp(17px,1.6vw,20px)', lineHeight: 1.55, color: 'rgba(255,255,255,.86)', maxWidth: '660px' }}>
            From our flagship product BIO-PLY™ to a growing pipeline of innovative biologics, every Qentoros therapy is
            guided by rigorous science, manufactured to uncompromising quality standards, and developed through
            meaningful clinical results for real-world impact.
          </p>
          </div>
        </div>
      </section>

      {/* AVAILABLE NOW */}
      <section style={{ background: CREAM, padding: 'clamp(64px,8vw,110px) 7vw' }}>
        <div className="q-reveal" style={{ maxWidth: '1180px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 'clamp(28px,3vw,40px)' }}>
            <span className="prod-live-dot" aria-hidden="true" />
            <p style={{ ...eyebrowStyle(MAROON) }}>Available Now</p>
          </div>
          <div
            className="prod-available-card"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.15fr .85fr',
              gap: 0,
              border: '1px solid #E4DFD7',
              borderRadius: '20px',
              overflow: 'hidden',
              background: '#fff',
              boxShadow: '0 30px 60px -40px rgba(26,25,23,.4)',
            }}
          >
            <div style={{ padding: 'clamp(32px,4vw,56px)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                <span style={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '.04em', color: '#2E7D5B', background: '#EAF3EE', border: '1px solid #CFE5D9', borderRadius: '100px', padding: '6px 13px' }}>
                  ✓ In clinical use
                </span>
                <span style={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '.04em', color: '#5C5853', background: CREAM, border: '1px solid #E9E4DC', borderRadius: '100px', padding: '6px 13px' }}>
                  Equine
                </span>
                <span style={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '.04em', color: '#5C5853', background: CREAM, border: '1px solid #E9E4DC', borderRadius: '100px', padding: '6px 13px' }}>
                  Intra-synovial
                </span>
              </div>
              <h2 style={{ fontFamily: EXP, fontWeight: 700, fontSize: 'clamp(32px,4vw,52px)', lineHeight: 1, letterSpacing: '-.025em', color: INK, marginBottom: '12px' }}>
                BIO-PLY™
              </h2>
              <p style={{ fontSize: 'clamp(16px,1.7vw,19px)', lineHeight: 1.6, color: '#5C5853', maxWidth: '460px', marginBottom: '28px' }}>
                The first-in-class, triple-action peptide biologic developed specifically for equine septic joints.
              </p>
              <BioPlyFacts />
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Link
                  to="/products/bio-ply"
                  className="q-cta"
                  style={{ background: MAROON, color: '#fff', textDecoration: 'none', fontSize: '15px', fontWeight: 600, padding: '14px 26px', borderRadius: '10px', border: `1px solid ${MAROON}`, transition: 'all .25s ease' }}
                >
                  More about BIO-PLY™
                </Link>
                <Link to="/order" className="prod-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: MAROON, textDecoration: 'none', fontSize: '15px', fontWeight: 600, transition: 'gap .25s ease' }}>
                  Request BIO-PLY™ <span className="q-arrow" aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
            <div className="prod-available-media" style={{ position: 'relative', minHeight: '340px', background: '#EDE8E1' }}>
              <img src={bioplyImageUrl} alt="Vials of BIO-PLY, a platelet-derived peptide biologic" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '38% center' }} />
            </div>
          </div>
        </div>
      </section>

      {/* PIPELINE — Q-Biologics™ development-status table. Each row is one
          program indication, and its stage is carried by a single colour-coded
          chip. The chip is the only status indicator — no rail, no dotted line,
          no stage columns — so nothing is stated twice. The legend below
          repeats the stage → colour mapping in words. */}
      <section id="pipeline" style={{ background: DARK, color: '#F5F3F0', padding: 'clamp(72px,9vw,130px) 7vw' }}>
        <div className="q-reveal" style={{ maxWidth: '1180px', margin: '0 auto' }}>
          <div style={{ marginBottom: 'clamp(36px,4vw,56px)' }}>
            <p style={{ fontSize: '12.5px', letterSpacing: '.18em', textTransform: 'uppercase', color: '#D9899A', fontWeight: 700, marginBottom: '18px' }}>
              The Q-Biologics™ Platform
            </p>
            <h2 className="q-pipe-title" style={{ fontFamily: EXP }}>
              Building the future of peptide biologics.
            </h2>
            <p style={{ fontSize: '16.5px', lineHeight: 1.6, color: '#B5B0AB', textWrap: 'pretty', maxWidth: '760px' }}>
              <strong style={{ color: '#F5F3F0', fontWeight: 600 }}>
                BIO-PLY™ is the first product to emerge from our platform.
              </strong>{' '}
              Additional peptide biologics are advancing through development to address infectious, inflammatory, and
              degenerative diseases in veterinary medicine. While we can&rsquo;t yet share every detail of these
              programs, we&rsquo;re excited to provide a preview of what&rsquo;s ahead.
            </p>
          </div>

          {/* Column headers. Purely presentational — each group is labelled by
              its platform name and each row reads species → indication →
              status in order, so this adds nothing for assistive tech. */}
          <div className="q-pipe-head" aria-hidden="true">
            <div className="q-pipe-hlabel">Platform</div>
            <div className="q-pipe-hlabel" style={{ paddingLeft: '26px' }}>Species</div>
            <div className="q-pipe-hlabel">Indication</div>
            <div className="q-pipe-hlabel">Development status</div>
          </div>

          {PIPELINE_GROUPS.map((group, gi) => (
            <div className="q-pipe-group" key={group.name} role="group" aria-labelledby={`q-pipe-platform-${gi}`}>
              <div className="q-pipe-platform">
                <div className="q-pipe-platform-name" id={`q-pipe-platform-${gi}`} style={{ fontFamily: EXP }}>
                  {group.name}
                </div>
                <div className="q-pipe-platform-source">{group.source}</div>
              </div>
              <div className="q-pipe-rows">
                {group.rows.map((row) => {
                  const stage = STAGE_COLORS[row.stage]
                  const status = stageStatusLabel(row)
                  return (
                    <div className="q-prow" key={`${group.name}-${row.indication}`}>
                      <div className="q-pipe-species">{row.species}</div>
                      <div className="q-pipe-indication">{row.indication}</div>
                      <div>
                        <span className="q-chip" style={{ background: stage.bg, borderColor: stage.border }}>
                          <span className="q-chip-dot" aria-hidden="true" style={{ background: stage.dot }}>
                            <span className="q-ping" style={{ background: stage.dot }} />
                          </span>
                          <span className="q-chip-label" style={{ color: stage.text }}>{status}</span>
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          <div className="q-pipe-legend">
            {PIPELINE_LEGEND.map((item, i) => {
              const stage = STAGE_COLORS[i + 1]
              return (
                <div key={item.label}>
                  <div className="q-pipe-legend-bar" aria-hidden="true" style={{ background: stage.dot }} />
                  <div className="q-pipe-legend-label" style={{ color: stage.text }}>{item.label}</div>
                  <div className="q-pipe-legend-desc">{item.desc}</div>
                </div>
              )
            })}
          </div>

          <div className="q-pipe-notes">
            <p>
              <span style={{ color: '#B5B0AB' }}>{FDA_PATHWAY_FOOTNOTE_LEAD}</span>{' '}
              {FDA_PATHWAY_FOOTNOTE_REST}
            </p>
            <p>
              {PIPELINE_DISCLAIMER_LEAD}{' '}
              <Link to="/contact" className="q-pipe-link">{PIPELINE_DISCLAIMER_LINK}</Link>{' '}
              {PIPELINE_DISCLAIMER_REST}
            </p>
          </div>
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <section id="notify" style={{ background: CREAM, padding: 'clamp(64px,8vw,110px) 7vw' }}>
        <div className="q-reveal" style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: '12.5px', letterSpacing: '.18em', textTransform: 'uppercase', color: MAROON, fontWeight: 700, marginBottom: '16px' }}>
            Stay in the loop
          </p>
          <h2 style={{ fontFamily: EXP, fontWeight: 700, fontSize: 'clamp(28px,4vw,46px)', lineHeight: 1.05, letterSpacing: '-.02em', color: INK, marginBottom: '14px' }}>
            Sign up for our newsletter.
          </h2>
          <p style={{ fontSize: '16.5px', lineHeight: 1.6, color: '#5C5853', marginBottom: '32px', maxWidth: '540px', marginLeft: 'auto', marginRight: 'auto' }}>
            Be the first to hear about new products, clinical trials, events we're attending, company milestones, and more — delivered straight to your inbox.
          </p>
          {subscribed ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '18px 28px', border: '1px solid #CFE5D9', borderRadius: '12px', background: '#EAF3EE' }}>
              <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#2E7D5B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>✓</span>
              <span style={{ fontSize: '15.5px', fontWeight: 600, color: '#1E5F44' }}>You're on the list. We'll be in touch.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} style={{ display: 'flex', gap: '10px', maxWidth: '460px', margin: '0 auto', flexWrap: 'wrap', justifyContent: 'center' }}>
              <input
                name="email"
                type="email"
                required
                aria-label="Your email address"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@clinic.com"
                className="q-field"
                style={{ flex: 1, minWidth: '240px', padding: '15px 18px', background: '#fff', border: '1px solid #E4DFD7', borderRadius: '10px', color: INK, fontFamily: "'Archivo',sans-serif", fontSize: '15px', transition: 'border-color .25s' }}
              />
              <button
                type="submit"
                className="q-cta"
                disabled={!captchaToken || submitting}
                style={{ background: MAROON, color: '#fff', border: `1px solid ${MAROON}`, fontSize: '15px', fontWeight: 600, padding: '15px 28px', borderRadius: '10px', cursor: !captchaToken || submitting ? 'not-allowed' : 'pointer', opacity: !captchaToken || submitting ? 0.6 : 1, fontFamily: "'Archivo',sans-serif", transition: 'all .25s ease' }}
              >
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
              {/* Left-aligned to the email field's edge rather than centred
                  under the whole form, so the widget reads as part of the input
                  it validates. */}
              <div style={{ flexBasis: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                <Turnstile
                  ref={turnstileRef}
                  onVerify={setCaptchaToken}
                  onExpire={() => setCaptchaToken('')}
                  onError={() => setCaptchaToken('')}
                />
              </div>
              {error && (
                <p role="alert" style={{ flexBasis: '100%', margin: 0, fontSize: '14px', color: '#B42318', textAlign: 'left' }}>
                  {error}
                </p>
              )}
            </form>
          )}
          <p style={{ fontSize: '15px', lineHeight: 1.6, color: '#6E6A65', marginTop: '28px' }}>
            Prefer social?{' '}
            <a
              href="https://www.linkedin.com/company/qentoros"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: MAROON, fontWeight: 600, textDecoration: 'none' }}
            >
              Follow us on LinkedIn
            </a>{' '}
            — we announce new products, clinical trials, events, and milestones there too.
          </p>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section style={{ background: '#fff', borderTop: '1px solid #ECE7DF', padding: 'clamp(56px,7vw,96px) 7vw' }}>
        <div className="prod-contact-grid q-reveal" style={{ maxWidth: '1180px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 'clamp(16px,2.4vw,24px)' }}>
          <Link to="/enroll" className="prod-card" style={{ textDecoration: 'none', color: 'inherit', display: 'block', padding: '34px 30px', borderRadius: '16px', background: CREAM, border: '1px solid #E9E4DC' }}>
            <p style={{ fontSize: '11.5px', letterSpacing: '.14em', textTransform: 'uppercase', color: MAROON, fontWeight: 700, marginBottom: '14px' }}>Get started</p>
            <h3 style={{ fontFamily: EXP, fontWeight: 700, fontSize: '22px', lineHeight: 1.12, marginBottom: '10px', letterSpacing: '-.01em' }}>Interested in BIO-PLY™?</h3>
            <p style={{ fontSize: '14.5px', lineHeight: 1.55, color: '#6E6A65', marginBottom: '18px' }}>Register to receive more information about using BIO-PLY™ in your clinic.</p>
            <span className="prod-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', fontWeight: 600, color: MAROON, transition: 'gap .25s ease' }}>
              Register <span className="q-arrow" aria-hidden="true">→</span>
            </span>
          </Link>
          <Link to="/contact" className="prod-card" style={{ textDecoration: 'none', color: '#F5F3F0', display: 'block', padding: '34px 30px', borderRadius: '16px', background: DARK }}>
            <p style={{ fontSize: '11.5px', letterSpacing: '.14em', textTransform: 'uppercase', color: '#D9899A', fontWeight: 700, marginBottom: '14px' }}>Everything else</p>
            <h3 style={{ fontFamily: EXP, fontWeight: 700, fontSize: '22px', lineHeight: 1.12, marginBottom: '10px', letterSpacing: '-.01em' }}>Get in touch</h3>
            <p style={{ fontSize: '14.5px', lineHeight: 1.55, color: '#B5B0AB', marginBottom: '18px' }}>Trials, partnerships, investment, or press, we'd love to hear from you.</p>
            <span className="prod-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', fontWeight: 600, color: '#D9899A', transition: 'gap .25s ease' }}>
              Contact us <span className="q-arrow" aria-hidden="true">→</span>
            </span>
          </Link>
        </div>
      </section>
      </main>

      <LandingFooter tagline="Harnessing the power of peptides to advance equine health." />
    </div>
  )
}
